import type { AssignmentSegment, RoundAssignment, RoundGroup } from "../../domain/round/types";
import { createVehicleVisualRegistry, vehicleVisualIdentity, type VehicleVisualIdentity } from "../../domain/transport/identity";
import type { TransportDirection, VehicleTrip } from "../../domain/transport/types";
import type { RoundWorkspacePayload } from "../../lib/api";

function safeFilename(value: string) {
  return value.normalize("NFKD").replace(/[^a-zA-Z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 90) || "export";
}

function xml(value: unknown) {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\"/g, "&quot;").replace(/'/g, "&apos;");
}

function clock(minutes: number | null | undefined) {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  const value = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function svgStart(width: number, height: number, background = "#eef3f5") {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="100%" height="100%" fill="${background}"/><style>text{font-family:Inter,Arial,sans-serif;fill:#10242d}.white{fill:#fff}.muted{fill:#687b86}.head{font-size:32px;font-weight:800}.sub{font-size:17px;font-weight:600}.tiny{font-size:13px}.small{font-size:15px}.body{font-size:17px}.strong{font-weight:800}.caps{font-size:12px;font-weight:800;letter-spacing:1.2px}.v1title{font-size:42px;font-weight:800;fill:#102638}.v1subtitle{font-size:24px;font-weight:800}.v1session{font-size:27px;font-weight:800}.v1section{font-size:21px;font-weight:800}.v1header{font-size:16px;font-weight:800}.v1cell{font-size:17px}.v1cellStrong{font-size:17px;font-weight:800}.v1small{font-size:14px}.v1smallStrong{font-size:14px;font-weight:800}.v1badge{font-size:13px;font-weight:800}</style>`;
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

async function svgToPng(svg: string, width: number, height: number, filename: string) {
  const source = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(source);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const scale = height > 8500 ? 1 : Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas jest niedostępny.");
    context.scale(scale, scale);
    context.fillStyle = "#fff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    const png = await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Nie udało się utworzyć PNG.")), "image/png", 0.97));
    downloadBlob(filename, png);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function peopleMap(workspace: RoundWorkspacePayload) {
  const result = new Map<string, string>();
  for (const row of workspace.staff) result.set(row.personId, row.displayName);
  for (const row of workspace.schedule?.people || []) result.set(row.personId, row.displayName);
  return result;
}

function tripPeople(trip: VehicleTrip) {
  return [trip.driverPersonId, ...trip.passengerPersonIds].filter(Boolean);
}

function personVehicleMap(workspace: RoundWorkspacePayload, direction: TransportDirection) {
  const result = new Map<string, string>();
  const trips = direction === "to-course" ? workspace.transport?.departures || [] : workspace.transport?.returns || [];
  for (const trip of trips) {
    for (const personId of tripPeople(trip)) {
      if (!result.has(personId)) result.set(personId, trip.vehicleId);
    }
  }
  return result;
}

const FULL = new Set<AssignmentSegment>(["full18", "extraFull18"]);
const FIRST_HALF = new Set<AssignmentSegment>(["holes1to9", "extra1to9"]);
const SECOND_HALF = new Set<AssignmentSegment>(["holes10to18", "extra10to18"]);

const PANEL = "#FFFFFF";
const PANEL_ALT = "#F5F8FA";
const INK = "#102638";
const MUTED = "#5C7182";
const NAVY = "#102E45";
const NAVY_SOFT = "#1C506B";
const GREEN = "#17624F";
const GRID = "#BFCBD4";
const GRID_STRONG = "#8295A3";
const WHITE = "#FFFFFF";
const TO_COURSE_ACCENT = "#15705e";
const RETURN_ACCENT = "#164d6b";
const TV_FILL = "#DCE4FF";
const TV_STRIPE = "#334FC5";
const TV_BADGE = "#263FA7";
const TEE_PIN_FILL = "#FFE9C8";
const TEE_PIN_BADGE = "#A85A08";
const HOLES36_FILL = "#FFF0DB";
const HOLES36_ACCENT = "#C2410C";

type AssignmentKey = "h1" | "h2" | "full" | "primary" | "secondaryFull" | "secondaryH1" | "secondaryH2";
type ColumnKey = "group" | "tee" | "start" | "finish9" | "finish18" | "status" | AssignmentKey;

interface ScoringColumn {
  key: ColumnKey;
  label: string;
  baseWidth: number;
}

let measureContext: CanvasRenderingContext2D | null = null;

function textWidth(value: unknown, size = 17, bold = false) {
  if (!measureContext) measureContext = document.createElement("canvas").getContext("2d");
  if (!measureContext) return String(value ?? "").length * size * 0.58;
  measureContext.font = `${bold ? 800 : 400} ${size}px Inter, Arial, sans-serif`;
  return measureContext.measureText(String(value ?? "")).width;
}

function fitText(value: unknown, maxWidth: number, size = 17, bold = false) {
  let text = String(value ?? "—");
  if (textWidth(text, size, bold) <= maxWidth) return text;
  const ellipsis = "…";
  while (text && textWidth(text + ellipsis, size, bold) > maxWidth) text = text.slice(0, -1);
  return text ? `${text.trimEnd()}${ellipsis}` : ellipsis;
}

function wrapText(value: unknown, maxWidth: number, size = 17, bold = false, maxLines = 4) {
  const text = String(value ?? "").trim();
  if (!text) return [""];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = `${current} ${word}`.trim();
    if (!current || textWidth(candidate, size, bold) <= maxWidth) current = candidate;
    else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (lines.length > maxLines) {
    lines.length = maxLines;
    lines[maxLines - 1] = fitText(lines[maxLines - 1], maxWidth, size, bold);
  }
  return lines.length ? lines : [""];
}

function groupStatus(group: RoundGroup) {
  if (group.teePin) return "TEE/PIN";
  if (group.tv || (group.requiredScorers || 1) > 1) return "TV";
  return "";
}

function assignmentsByGroup(workspace: RoundWorkspacePayload) {
  const result = new Map<string, RoundAssignment[]>();
  for (const assignment of workspace.schedule?.assignments || []) {
    const rows = result.get(assignment.groupId) || [];
    rows.push(assignment);
    result.set(assignment.groupId, rows);
  }
  return result;
}

function assignmentRows(
  group: RoundGroup,
  key: AssignmentKey,
  byGroup: Map<string, RoundAssignment[]>,
) {
  const rows = byGroup.get(group.id) || [];
  if (key === "h1") return rows.filter((row) => FIRST_HALF.has(row.segment));
  if (key === "h2") return rows.filter((row) => SECOND_HALF.has(row.segment));
  if (key === "full") return rows.filter((row) => FULL.has(row.segment));
  if (key === "primary") {
    const primary = rows.find((row) => row.segment === "full18");
    return primary ? [primary] : [];
  }
  if (key === "secondaryFull") {
    let skippedPrimary = false;
    return rows.filter((row) => {
      if (row.segment === "extraFull18") return true;
      if (row.segment !== "full18") return false;
      if (!skippedPrimary) {
        skippedPrimary = true;
        return false;
      }
      return true;
    });
  }
  if (key === "secondaryH1") return rows.filter((row) => FIRST_HALF.has(row.segment));
  return rows.filter((row) => SECOND_HALF.has(row.segment));
}

function assignmentNames(
  group: RoundGroup,
  key: AssignmentKey,
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
) {
  return assignmentRows(group, key, byGroup).map((row) => people.get(row.personId) || row.personId);
}

function hasAssignment(
  groups: RoundGroup[],
  key: AssignmentKey,
  byGroup: Map<string, RoundAssignment[]>,
) {
  return groups.some((group) => assignmentRows(group, key, byGroup).length > 0);
}

function _holeAfter(tee: number, offset: number): number {
  return ((tee - 1 + offset) % 18) + 1;
}

function _inferFirstHalfHoles(group: { startMinutes: number; finish9Minutes?: number | null; finish18Minutes?: number | null }): number {
  const start = group.startMinutes;
  const total = (group.finish18Minutes ?? start) - start;
  const toFirst = (group.finish9Minutes ?? start) - start;
  if (total <= 0) return 9;
  return Math.min(17, Math.max(1, Math.round((toFirst / total) * 18)));
}

function _holeRangeLabel(start: number, end: number): string {
  if (end >= start) return `${start}–${end}`;
  if (start === 18) return `18, 1–${end}`;
  return `${start}–18, 1–${end}`;
}

// Not every course splits at tee 1 / tee 10 (Wentworth West for BMW PGA 2026
// splits at tee 1 / tee 9, 8+10 not 9+9) - derive the real column labels from
// each group's own stored times instead of assuming the split point.
function tableRanges(groups: RoundGroup[]): readonly [string, string] {
  const byTee = new Map<number, RoundGroup>();
  for (const group of groups) {
    const tee = Number(group.tee);
    if (tee && !byTee.has(tee)) byTee.set(tee, group);
  }
  if (byTee.size === 0) return ["1–9", "10–18"] as const;
  const labelsPerTee = [...byTee.entries()].map(([tee, group]) => {
    const firstHalf = _inferFirstHalfHoles(group);
    const firstEnd = _holeAfter(tee, firstHalf - 1);
    const secondStart = _holeAfter(tee, firstHalf);
    const secondEnd = _holeAfter(tee, 17);
    return [_holeRangeLabel(tee, firstEnd), _holeRangeLabel(secondStart, secondEnd)] as const;
  });
  const first = labelsPerTee[0];
  const allSame = labelsPerTee.every(([a, b]) => a === first[0] && b === first[1]);
  return allSame ? first : (["1. połowa dnia", "2. połowa dnia"] as const);
}

function scoringColumns(
  groups: RoundGroup[],
  weekend: boolean,
  byGroup: Map<string, RoundAssignment[]>,
) {
  const [firstRange, secondRange] = tableRanges(groups);
  const columns: ScoringColumn[] = [
    { key: "group", label: "GRUPA", baseWidth: 90 },
    { key: "tee", label: "TEE", baseWidth: 65 },
    { key: "start", label: "START", baseWidth: 95 },
    { key: "finish9", label: "KONIEC 9", baseWidth: 105 },
    { key: "finish18", label: "KONIEC 18", baseWidth: 115 },
  ];
  if (weekend) {
    if (hasAssignment(groups, "primary", byGroup)) columns.push({ key: "primary", label: "PODSTAWOWY 18", baseWidth: 250 });
    if (hasAssignment(groups, "secondaryFull", byGroup)) columns.push({ key: "secondaryFull", label: "DODATKOWY 18", baseWidth: 260 });
    if (hasAssignment(groups, "secondaryH1", byGroup)) columns.push({ key: "secondaryH1", label: firstRange, baseWidth: 235 });
    if (hasAssignment(groups, "secondaryH2", byGroup)) columns.push({ key: "secondaryH2", label: secondRange, baseWidth: 235 });
  } else {
    if (hasAssignment(groups, "h1", byGroup)) columns.push({ key: "h1", label: firstRange, baseWidth: 250 });
    if (hasAssignment(groups, "h2", byGroup)) columns.push({ key: "h2", label: secondRange, baseWidth: 250 });
    if (hasAssignment(groups, "full", byGroup)) columns.push({ key: "full", label: "PEŁNE 18", baseWidth: 250 });
  }
  if (groups.some((group) => groupStatus(group))) columns.push({ key: "status", label: "STATUS", baseWidth: 110 });
  return columns;
}

function scaledWidths(columns: ScoringColumn[], targetWidth: number) {
  const widths = columns.map((column) => column.baseWidth);
  const baseTotal = widths.reduce((sum, value) => sum + value, 0);
  const flexible = columns
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => ["primary", "secondaryFull", "secondaryH1", "secondaryH2", "h1", "h2", "full"].includes(column.key))
    .map(({ index }) => index);
  if (baseTotal < targetWidth) {
    const extra = targetWidth - baseTotal;
    const targets = flexible.length ? flexible : [widths.length - 1];
    for (const index of targets) widths[index] += Math.floor(extra / targets.length);
    widths[widths.length - 1] += targetWidth - widths.reduce((sum, value) => sum + value, 0);
    return widths;
  }
  const ratio = targetWidth / Math.max(1, baseTotal);
  const result = widths.map((value) => Math.max(62, Math.floor(value * ratio)));
  result[result.length - 1] += targetWidth - result.reduce((sum, value) => sum + value, 0);
  return result;
}

function isAssignmentKey(key: ColumnKey): key is AssignmentKey {
  return ["h1", "h2", "full", "primary", "secondaryFull", "secondaryH1", "secondaryH2"].includes(key);
}

function columnText(group: RoundGroup, key: ColumnKey) {
  if (key === "group") return String(group.number);
  if (key === "tee") return group.tee == null ? "—" : String(group.tee);
  if (key === "start") return clock(group.startMinutes);
  if (key === "finish9") return clock(group.finish9Minutes);
  if (key === "finish18") return clock(group.finish18Minutes);
  if (key === "status") return groupStatus(group);
  return "";
}

function rowHeight(
  group: RoundGroup,
  columns: ScoringColumn[],
  widths: number[],
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
) {
  let height = 48;
  columns.forEach((column, index) => {
    if (isAssignmentKey(column.key)) {
      const units = Math.max(1, assignmentNames(group, column.key, byGroup, people).length);
      height = Math.max(height, 16 + units * 31);
    } else {
      const bold = column.key === "group" || groupStatus(group) === "TV";
      const units = Math.max(1, wrapText(columnText(group, column.key), Math.max(28, widths[index] - 18), 17, bold).length);
      height = Math.max(height, 14 + units * 22);
    }
  });
  return height;
}

function scheduleTableHeight(
  groups: RoundGroup[],
  width: number,
  weekend: boolean,
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
) {
  if (!groups.length) return 0;
  const columns = scoringColumns(groups, weekend, byGroup);
  const widths = scaledWidths(columns, width);
  return 92 + groups.reduce((sum, group) => sum + rowHeight(group, columns, widths, byGroup, people), 0);
}

function svgTextLines(
  x: number,
  y: number,
  lines: string[],
  className: string,
  fill = INK,
  lineHeight = 22,
) {
  return lines.map((line, index) => `<text x="${x}" y="${y + index * lineHeight}" class="${className}" style="fill:${fill}">${xml(line)}</text>`).join("");
}

function svgBadge(x: number, y: number, label: string, fill: string) {
  const width = Math.max(54, 22 + label.length * 8);
  return `<rect x="${x}" y="${y}" width="${width}" height="28" rx="8" fill="${fill}"/><text x="${x + width / 2}" y="${y + 19}" text-anchor="middle" class="v1badge white">${xml(label)}</text>`;
}

function drawAssignmentCell(
  group: RoundGroup,
  key: AssignmentKey,
  left: number,
  top: number,
  right: number,
  bottom: number,
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
  holes36: Set<string>,
  vehicleByPerson: Map<string, string>,
  vehicleColors: ReadonlyMap<string, VehicleVisualIdentity>,
) {
  const rows = assignmentRows(group, key, byGroup);
  if (!rows.length) return `<text x="${left + 9}" y="${top + 27}" class="v1cell" style="fill:${INK}">—</text>`;
  const bold = key === "primary" || ["h1", "h2", "full"].includes(key) || groupStatus(group) === "TV";
  let svg = "";
  let cursorY = top + 8;
  for (const row of rows) {
    const name = people.get(row.personId) || row.personId;
    const is36 = holes36.has(row.personId);
    const vehicleId = vehicleByPerson.get(row.personId) || "";
    const vehicleVisual = vehicleId ? vehicleVisualIdentity(vehicleId, vehicleColors) : null;
    const pillRight = right - 7;
    const pillBottom = Math.min(bottom - 5, cursorY + 25);
    const pillHeight = Math.max(18, pillBottom - cursorY + 2);

    if (vehicleVisual) {
      svg += `<rect x="${left + 6}" y="${cursorY - 2}" width="${Math.max(16, pillRight - left - 6)}" height="${pillHeight}" rx="7" fill="${vehicleVisual.background}" stroke="${vehicleVisual.border}" stroke-width="1.5"/>`;
      svg += `<rect x="${left + 6}" y="${cursorY - 2}" width="6" height="${pillHeight}" rx="3" fill="${vehicleVisual.border}"/>`;
      const badgeWidth = is36 ? 42 : 0;
      if (is36) {
        svg += `<rect x="${pillRight - badgeWidth - 5}" y="${cursorY + 1}" width="${badgeWidth + 1}" height="21" rx="6" fill="${HOLES36_ACCENT}"/>`;
        svg += `<text x="${pillRight - badgeWidth / 2 - 4}" y="${cursorY + 16}" text-anchor="middle" class="v1badge white">36</text>`;
      }
      const available = Math.max(24, pillRight - left - 24 - (is36 ? badgeWidth + 8 : 0));
      svg += `<text x="${left + 17}" y="${cursorY + 18}" class="${bold || row.locked || is36 ? "v1cellStrong" : "v1cell"}" style="fill:${vehicleVisual.foreground}">${xml(fitText(name, available, 17, bold || row.locked || is36))}</text>`;
    } else if (is36) {
      svg += `<rect x="${left + 6}" y="${cursorY - 2}" width="${Math.max(16, pillRight - left - 6)}" height="${pillHeight}" rx="7" fill="${HOLES36_FILL}"/>`;
      svg += `<rect x="${left + 6}" y="${cursorY - 2}" width="6" height="${pillHeight}" rx="3" fill="${HOLES36_ACCENT}"/>`;
      const badgeWidth = 42;
      svg += `<rect x="${pillRight - badgeWidth - 5}" y="${cursorY + 1}" width="${badgeWidth + 1}" height="21" rx="6" fill="${HOLES36_ACCENT}"/>`;
      svg += `<text x="${pillRight - badgeWidth / 2 - 4}" y="${cursorY + 16}" text-anchor="middle" class="v1badge white">36</text>`;
      const available = Math.max(24, pillRight - badgeWidth - left - 30);
      svg += `<text x="${left + 17}" y="${cursorY + 18}" class="v1cellStrong" style="fill:${INK}">${xml(fitText(name, available, 17, true))}</text>`;
    } else {
      svg += `<text x="${left + 9}" y="${cursorY + 18}" class="${bold || row.locked ? "v1cellStrong" : "v1cell"}" style="fill:${INK}">${xml(fitText(name, Math.max(28, right - left - 18), 17, bold || row.locked))}</text>`;
    }
    if ((row.source as string) === "reserve") {
      svg += `<rect x="${left + 4}" y="${cursorY - 4}" width="${Math.max(16, pillRight - left - 2)}" height="${pillHeight + 4}" rx="8" fill="none" stroke="#E07B00" stroke-width="3.5"/>`;
      svg += `<rect x="${left + 4}" y="${cursorY - 4}" width="46" height="15" rx="4" fill="#E07B00"/><text x="${left + 27}" y="${cursorY + 8}" text-anchor="middle" class="v1badge white" style="font-size:11px">NOWY</text>`;
    }
    cursorY += 31;
  }
  return svg;
}

function drawScheduleTable(
  x: number,
  y: number,
  width: number,
  title: string,
  groups: RoundGroup[],
  weekend: boolean,
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
  holes36: Set<string>,
  vehicleByPerson: Map<string, string>,
  vehicleColors: ReadonlyMap<string, VehicleVisualIdentity>,
) {
  if (!groups.length) return { svg: "", height: 0 };
  const columns = scoringColumns(groups, weekend, byGroup);
  const widths = scaledWidths(columns, width);
  const heights = groups.map((group) => rowHeight(group, columns, widths, byGroup, people));
  const titleHeight = 48;
  const headerHeight = 44;
  const titleFill = weekend ? TO_COURSE_ACCENT : "#EAF0F4";
  const titleTextFill = weekend ? WHITE : INK;
  const titleMetaFill = weekend ? WHITE : MUTED;
  const totalHeight = titleHeight + headerHeight + heights.reduce((sum, value) => sum + value, 0);
  let svg = `<rect x="${x}" y="${y}" width="${width}" height="${totalHeight}" rx="12" fill="${PANEL}" stroke="${GRID_STRONG}" stroke-width="2"/>`;
  svg += `<rect x="${x}" y="${y}" width="${width}" height="${titleHeight}" rx="12" fill="${titleFill}"/><rect x="${x}" y="${y + titleHeight - 12}" width="${width}" height="12" fill="${titleFill}"/>`;
  svg += `<text x="${x + 15}" y="${y + 31}" class="v1section" style="fill:${titleTextFill}">${xml(title)}</text>`;
  svg += `<text x="${x + width - 15}" y="${y + 30}" text-anchor="end" class="v1smallStrong" style="fill:${titleMetaFill}">${groups.length} grup</text>`;

  let cursorX = x;
  const headerY = y + titleHeight;
  columns.forEach((column, index) => {
    const right = index === columns.length - 1 ? x + width : cursorX + widths[index];
    svg += `<rect x="${cursorX}" y="${headerY}" width="${right - cursorX}" height="${headerHeight}" fill="${NAVY}" stroke="${GRID}" stroke-width="1"/>`;
    svg += `<text x="${cursorX + 8}" y="${headerY + 29}" class="v1header white">${xml(column.label)}</text>`;
    cursorX = right;
  });

  let cursorY = headerY + headerHeight;
  groups.forEach((group, rowIndex) => {
    const status = groupStatus(group);
    const fill = status === "TV" ? TV_FILL : status === "TEE/PIN" ? TEE_PIN_FILL : rowIndex % 2 ? PANEL_ALT : PANEL;
    const rowH = heights[rowIndex];
    cursorX = x;
    columns.forEach((column, columnIndex) => {
      const right = columnIndex === columns.length - 1 ? x + width : cursorX + widths[columnIndex];
      svg += `<rect x="${cursorX}" y="${cursorY}" width="${right - cursorX}" height="${rowH}" fill="${fill}" stroke="${GRID}" stroke-width="1"/>`;
      if (columnIndex === 0 && status === "TV") svg += `<rect x="${cursorX}" y="${cursorY}" width="8" height="${rowH}" fill="${TV_STRIPE}"/>`;
      else if (columnIndex === 0 && status === "TEE/PIN") svg += `<rect x="${cursorX}" y="${cursorY}" width="8" height="${rowH}" fill="${TEE_PIN_BADGE}"/>`;

      if (column.key === "status" && status) {
        svg += svgBadge(cursorX + 9, cursorY + 8, status, status === "TV" ? TV_BADGE : TEE_PIN_BADGE);
      } else if (isAssignmentKey(column.key)) {
        svg += drawAssignmentCell(group, column.key, cursorX, cursorY, right, cursorY + rowH, byGroup, people, holes36, vehicleByPerson, vehicleColors);
      } else {
        const bold = column.key === "group" || status === "TV";
        const lines = wrapText(columnText(group, column.key), Math.max(28, right - cursorX - 18), 17, bold);
        svg += svgTextLines(cursorX + 9, cursorY + 27, lines, bold ? "v1cellStrong" : "v1cell", INK);
      }
      cursorX = right;
    });
    cursorY += rowH;
  });

  return { svg, height: totalHeight };
}

function documentHeader(width: number, tournamentName: string, round: string, groupCount: number) {
  const margin = 46;
  const meta = `${round} · ${groupCount} grup`;
  const metaWidth = Math.max(210, textWidth(meta, 24, true) + 34);
  let svg = `<rect x="${margin}" y="22" width="${width - margin * 2}" height="90" rx="16" fill="${PANEL}" stroke="${GRID_STRONG}" stroke-width="2"/>`;
  svg += `<rect x="${margin}" y="22" width="9" height="90" fill="${GREEN}"/>`;
  svg += `<text x="${margin + 26}" y="70" class="v1title" style="fill:${INK}">${xml(tournamentName)}</text>`;
  svg += `<text x="${margin + 28}" y="103" class="v1subtitle" style="fill:${NAVY_SOFT}">Rozpiska scoringowa</text>`;
  const right = width - margin - 20;
  svg += `<rect x="${right - metaWidth}" y="47" width="${metaWidth}" height="40" rx="10" fill="#E7EFF4"/>`;
  svg += `<text x="${right - metaWidth + 17}" y="76" class="v1subtitle" style="fill:${NAVY}">${xml(meta)}</text>`;
  return svg;
}

const SESSION_GAP_MINUTES = 90;

function detectSessionGroups(groups: RoundGroup[]): { morning: RoundGroup[]; afternoon: RoundGroup[] } | null {
  if (groups.length < 2) return null;
  const sorted = [...groups].sort((a, b) => a.startMinutes - b.startMinutes);
  let gapIndex = -1;
  let gapSize = 0;
  for (let i = 1; i < sorted.length; i += 1) {
    const gap = sorted[i].startMinutes - sorted[i - 1].startMinutes;
    if (gap > gapSize) {
      gapSize = gap;
      gapIndex = i;
    }
  }
  if (gapIndex < 0 || gapSize < SESSION_GAP_MINUTES) return null;
  const first = sorted.slice(0, gapIndex);
  const second = sorted.slice(gapIndex);
  if (!first.length || !second.length) return null;
  // The groups are sorted by start time, so the earlier chunk is always the
  // morning wave and the later chunk the afternoon one - a wave's own start
  // time (e.g. an 11:10 second wave) says nothing about which side of noon
  // it lands on, so it must not gate the split. Only the gap size (checked
  // above) decides whether two real waves exist at all.
  return { morning: first, afternoon: second };
}

function sessionHeight(
  groups: RoundGroup[],
  width: number,
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
) {
  let total = 76;
  const sections = [
    groups.filter((group) => group.tee === 1),
    groups.filter((group) => group.tee === 10),
    groups.filter((group) => group.tee !== 1 && group.tee !== 10),
  ];
  for (const rows of sections) if (rows.length) total += 18 + scheduleTableHeight(rows, width - 32, false, byGroup, people);
  return total + 18;
}

function drawSession(
  x: number,
  y: number,
  width: number,
  title: string,
  groups: RoundGroup[],
  afternoon: boolean,
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
  holes36: Set<string>,
  vehicleByPerson: Map<string, string>,
  vehicleColors: ReadonlyMap<string, VehicleVisualIdentity>,
) {
  const height = sessionHeight(groups, width, byGroup, people);
  const fill = afternoon ? RETURN_ACCENT : TO_COURSE_ACCENT;
  let svg = `<rect x="${x}" y="${y}" width="${width}" height="${height}" rx="15" fill="${PANEL}" stroke="${GRID_STRONG}" stroke-width="2"/>`;
  svg += `<rect x="${x}" y="${y}" width="${width}" height="62" rx="15" fill="${fill}"/><rect x="${x}" y="${y + 47}" width="${width}" height="15" fill="${fill}"/>`;
  svg += `<text x="${x + 18}" y="${y + 40}" class="v1session white">${xml(title)}</text>`;
  svg += `<text x="${x + width - 18}" y="${y + 37}" text-anchor="end" class="v1header white">${groups.length} grup</text>`;
  let cursorY = y + 76;
  const sections: Array<[string, RoundGroup[]]> = [
    ["Tee 1", groups.filter((group) => group.tee === 1)],
    ["Tee 10", groups.filter((group) => group.tee === 10)],
    ["Inne tee", groups.filter((group) => group.tee !== 1 && group.tee !== 10)],
  ];
  for (const [sectionTitle, rows] of sections) {
    if (!rows.length) continue;
    cursorY += 18;
    const table = drawScheduleTable(x + 16, cursorY, width - 32, sectionTitle, rows, false, byGroup, people, holes36, vehicleByPerson, vehicleColors);
    svg += table.svg;
    cursorY += table.height;
  }
  return { svg, height };
}

function weekendLayout(
  groups: RoundGroup[],
  byGroup: Map<string, RoundAssignment[]>,
  people: Map<string, string>,
  weekend: boolean,
) {
  const tee1 = groups.filter((group) => group.tee === 1).sort((a, b) => a.number - b.number || a.startMinutes - b.startMinutes);
  const tee10 = groups.filter((group) => group.tee === 10).sort((a, b) => a.number - b.number || a.startMinutes - b.startMinutes);
  const other = groups.filter((group) => group.tee !== 1 && group.tee !== 10).sort((a, b) => a.number - b.number || a.startMinutes - b.startMinutes);
  const width = 3400;
  const margin = 46;
  const gap = 34;
  const fullWidth = width - margin * 2;
  const layout: Array<{ x: number; y: number; width: number; title: string; groups: RoundGroup[] }> = [];
  const primary = [["Tee 1", tee1], ["Tee 10", tee10]] as const;
  const present = primary.filter(([, rows]) => rows.length);
  let cursorY = 132;
  if (present.length === 2) {
    const columnWidth = Math.floor((fullWidth - gap) / 2);
    layout.push({ x: margin, y: cursorY, width: columnWidth, title: present[0][0], groups: present[0][1] });
    layout.push({ x: margin + columnWidth + gap, y: cursorY, width: columnWidth, title: present[1][0], groups: present[1][1] });
    cursorY += Math.max(
      scheduleTableHeight(present[0][1], columnWidth, weekend, byGroup, people),
      scheduleTableHeight(present[1][1], columnWidth, weekend, byGroup, people),
    );
  } else if (present.length === 1) {
    layout.push({ x: margin, y: cursorY, width: fullWidth, title: present[0][0], groups: present[0][1] });
    cursorY += scheduleTableHeight(present[0][1], fullWidth, weekend, byGroup, people);
  }
  if (other.length) {
    if (layout.length) cursorY += gap;
    layout.push({ x: margin, y: cursorY, width: fullWidth, title: present.length ? "Inne tee" : "Rozpiska", groups: other });
    cursorY += scheduleTableHeight(other, fullWidth, weekend, byGroup, people);
  }
  if (!layout.length) cursorY += 220;
  return { layout, height: cursorY + margin };
}

export async function exportOperationalScoringPng(workspace: RoundWorkspacePayload, tournamentName: string) {
  const schedule = workspace.schedule;
  if (!schedule) throw new Error("Brak rozpiski do eksportu.");
  const width = 3400;
  const margin = 46;
  const gap = 34;
  const byGroup = assignmentsByGroup(workspace);
  const people = peopleMap(workspace);
  const holes36 = new Set(schedule.people.filter((row) => row.holes >= 36).map((row) => row.personId));
  const weekend = schedule.sessionModel.kind === "weekend-single-session" || workspace.round === "R3" || workspace.round === "R4";
  const toCourseVehicles = personVehicleMap(workspace, "to-course");
  const returnVehicles = personVehicleMap(workspace, "return");
  const vehicleColors = createVehicleVisualRegistry([...toCourseVehicles.values(), ...returnVehicles.values()]);

  // Weekend rounds keep the plain Tee1/Tee10 layout (the "weekend" flag only
  // ever controlled the column set, never session panels). For weekday
  // rounds, only wrap in MORNING/AFTERNOON session panels when the groups
  // genuinely cluster into two clock-time-separated waves - a single tight
  // morning block (like ADLC's 9:00-11:23) must never get a fake session
  // label just because the backend's sessionModel happens to carry two
  // session indices.
  const sessions = weekend ? null : detectSessionGroups(schedule.groups);

  if (sessions) {
    const sessionWidth = Math.floor((width - margin * 2 - gap) / 2);
    const morningHeight = sessions.morning.length ? sessionHeight(sessions.morning, sessionWidth, byGroup, people) : 0;
    const afternoonHeight = sessions.afternoon.length ? sessionHeight(sessions.afternoon, sessionWidth, byGroup, people) : 0;
    const contentHeight = Math.max(morningHeight, afternoonHeight, 220);
    const sessionTotalHeight = 132 + contentHeight + margin;
    let sessionSvg = svgStart(width, sessionTotalHeight, "#E8EEF3");
    sessionSvg += documentHeader(width, tournamentName, workspace.round, schedule.groups.length);
    if (sessions.morning.length) {
      const panel = drawSession(margin, 132, sessionWidth, "MORNING SESSION", sessions.morning, false, byGroup, people, holes36, toCourseVehicles, vehicleColors);
      sessionSvg += panel.svg;
    }
    if (sessions.afternoon.length) {
      const panel = drawSession(margin + sessionWidth + gap, 132, sessionWidth, "AFTERNOON SESSION", sessions.afternoon, true, byGroup, people, holes36, returnVehicles, vehicleColors);
      sessionSvg += panel.svg;
    }
    sessionSvg += `</svg>`;
    await svgToPng(sessionSvg, width, sessionTotalHeight, `${safeFilename(tournamentName)}-${workspace.round}-scoring.png`);
    return;
  }

  const { layout, height } = weekendLayout(schedule.groups, byGroup, people, weekend);
  let svg = svgStart(width, height, "#E8EEF3");
  svg += documentHeader(width, tournamentName, workspace.round, schedule.groups.length);
  for (const section of layout) {
    const table = drawScheduleTable(section.x, section.y, section.width, section.title, section.groups, weekend, byGroup, people, holes36, toCourseVehicles, vehicleColors);
    svg += table.svg;
  }
  if (!layout.length) {
    svg += `<rect x="${margin}" y="150" width="${width - margin * 2}" height="180" rx="12" fill="${PANEL}" stroke="${GRID_STRONG}" stroke-width="2"/>`;
    svg += `<text x="${margin + 24}" y="250" class="v1section" style="fill:${MUTED}">Brak grup w rozpisce.</text>`;
  }
  svg += `</svg>`;
  await svgToPng(svg, width, height, `${safeFilename(tournamentName)}-${workspace.round}-scoring.png`);
}

function directionMeta(direction: TransportDirection) {
  return direction === "to-course"
    ? { title: "Transport — dojazd na pole", short: "dojazd", accent: TO_COURSE_ACCENT, metric: "morningMaxWaitMinutes" as const }
    : { title: "Transport — powrót z pola", short: "powrot", accent: RETURN_ACCENT, metric: "returnMaxWaitMinutes" as const };
}

export async function exportOperationalTransportPng(workspace: RoundWorkspacePayload, tournamentName: string, direction: TransportDirection) {
  const plan = workspace.transport;
  if (!plan) throw new Error("Brak transportu do eksportu.");
  const trips = direction === "to-course" ? plan.departures : plan.returns;
  if (!trips.length) throw new Error(direction === "to-course" ? "Brak dojazdów do eksportu." : "Brak powrotów do eksportu.");
  const meta = directionMeta(direction);
  const people = peopleMap(workspace);
  const vehicles = new Map(workspace.resources.vehicles.map((row) => [row.id, row]));
  const bases = new Map(workspace.resources.bases.map((row) => [row.id, row.name]));
  const vehicleColors = createVehicleVisualRegistry(trips.map((trip) => trip.vehicleId));
  const orderedTrips = [...trips].sort((a, b) => a.departAtMinutes - b.departAtMinutes);
  const width = 1760;
  const header = 142;
  const carHeader = 74;
  const personRow = 34;
  const carGap = 16;
  const tripHeights = trips.map((trip) => carHeader + Math.max(1, tripPeople(trip).length) * personRow + carGap);
  const height = header + tripHeights.reduce((sum, value) => sum + value, 0) + 38;
  let svg = svgStart(width, height);
  svg += `<rect width="${width}" height="116" fill="#ffffff"/><rect x="24" y="18" width="6" height="76" fill="${meta.accent}"/><text x="46" y="54" class="head">${xml(tournamentName)}</text><text x="46" y="82" class="sub">${xml(meta.title)} · ${workspace.round}</text><rect x="1530" y="25" width="220" height="54" rx="12" fill="#e6eff2"/><text x="1640" y="47" text-anchor="middle" class="caps">MAX CZEKanie</text><text x="1640" y="70" text-anchor="middle" class="body strong">${plan.metrics[meta.metric]} min</text>`;
  let y = header;
  for (const trip of orderedTrips) {
    const vehicle = vehicles.get(trip.vehicleId);
    const visual = vehicleVisualIdentity(trip.vehicleId, vehicleColors);
    const base = bases.get(trip.baseId) || vehicle?.baseName || trip.baseId || "—";
    const members = tripPeople(trip);
    const isUber = trip.vehicleId.startsWith("uber-");
    const isXL = isUber && members.length > 3;
    const blockHeight = carHeader + Math.max(1, members.length) * personRow;
    svg += `<rect x="24" y="${y}" width="1712" height="${blockHeight}" rx="12" fill="#fff" stroke="#cbd8dd"/><rect x="24" y="${y}" width="1712" height="42" rx="12" fill="${visual.background}" stroke="${visual.border}" stroke-width="1.5"/><rect x="24" y="${y + 32}" width="1712" height="10" fill="${visual.background}"/>`;
    svg += `<text x="42" y="${y + 27}" class="body strong" style="fill:${visual.foreground}">${xml(vehicle?.name || trip.vehicleId)}${isXL ? " · XL" : ""}</text><text x="1720" y="${y + 27}" text-anchor="end" class="tiny strong" style="fill:${visual.foreground}">${members.length} os. · ${trip.shuttle ? `SHUTTLE ${trip.tripNumber}` : `kurs ${trip.tripNumber}`}</text>`;
    svg += `<text x="42" y="${y + 61}" class="tiny muted">${direction === "to-course" ? "WYJAZD" : "ODJAZD"}</text><text x="120" y="${y + 61}" class="small strong">${clock(trip.departAtMinutes)}</text><text x="260" y="${y + 61}" class="tiny muted">PRZYJAZD</text><text x="340" y="${y + 61}" class="small strong">${clock(trip.arriveAtMinutes)}</text><text x="500" y="${y + 61}" class="tiny muted">BAZA</text><text x="555" y="${y + 61}" class="small strong">${xml(base)}</text><text x="1720" y="${y + 61}" text-anchor="end" class="small strong">spread ${trip.waitSpreadMinutes} min</text>`;
    let py = y + carHeader;
    for (let index = 0; index < Math.max(1, members.length); index += 1) {
      const personId = members[index] || "";
      const isDriver = personId === trip.driverPersonId;
      svg += `<rect x="34" y="${py}" width="1692" height="${personRow - 2}" fill="${isDriver ? visual.background : index % 2 ? "#fbfdfe" : "#f3f7f8"}"/>`;
      svg += `<text x="48" y="${py + 22}" class="tiny muted">${index + 1}</text><text x="82" y="${py + 22}" class="small ${isDriver ? "strong" : ""}">${xml(personId ? people.get(personId) || personId : "Brak osób")}</text><text x="1708" y="${py + 22}" text-anchor="end" class="tiny ${isDriver ? "strong" : "muted"}">${isDriver ? (isUber ? "ZAMAWIA" : "KIEROWCA") : "PASAŻER"}</text>`;
      py += personRow;
    }
    y += blockHeight + carGap;
  }
  svg += `</svg>`;
  await svgToPng(svg, width, height, `${safeFilename(tournamentName)}-${workspace.round}-transport-${meta.short}.png`);
}


export interface ReserveChangeRow { group: number; tee: number | null; time: string; segment: string; tv: boolean; was: string; now: string }

/** Osobny obraz: co dokładnie zmienia się względem zatwierdzonej rozpiski (plan rezerwowy). */
export async function exportReserveChangesPng(tournamentName: string, round: string, absent: string[], released: string[], rows: ReserveChangeRow[], duty: Array<{ name: string; was: string; now: string; earlier: boolean }>) {
  const width = 2000;
  const margin = 46;
  const rowH = 44;
  const dutyRows = duty.length;
  const height = 250 + released.length * 34 + 90 + rows.length * rowH + (dutyRows ? 90 + dutyRows * 36 : 0) + margin;
  let svg = svgStart(width, height, "#E8EEF3");
  svg += `<rect x="${margin}" y="22" width="${width - margin * 2}" height="110" rx="16" fill="${PANEL}" stroke="${GRID_STRONG}" stroke-width="2"/><rect x="${margin}" y="22" width="9" height="110" fill="#E07B00"/>`;
  svg += `<text x="${margin + 26}" y="70" class="v1title" style="fill:${INK}">${xml(tournamentName)} · ${xml(round)} · PLAN REZERWOWY</text>`;
  svg += `<text x="${margin + 28}" y="108" class="v1subtitle" style="fill:#B85F00">Nie przyjdą: ${xml(absent.join(", "))}</text>`;
  let y = 176;
  svg += `<text x="${margin}" y="${y}" class="v1section" style="fill:${INK}">ZDEJMUJEMY Z PLANU</text>`;
  y += 34;
  for (const item of released) { svg += `<text x="${margin + 18}" y="${y}" class="v1cell" style="fill:${INK}">• ${xml(item)}</text>`; y += 34; }
  if (!released.length) { svg += `<text x="${margin + 18}" y="${y}" class="v1cell" style="fill:${INK}">— nic</text>`; y += 34; }
  y += 30;
  svg += `<text x="${margin}" y="${y}" class="v1section" style="fill:${INK}">NOWE PRZYDZIAŁY (${rows.length})</text>`;
  y += 18;
  const cols = [margin, margin + 130, margin + 210, margin + 420, margin + 900, margin + 1330];
  const heads = ["GRUPA", "TEE", "GODZINY", "ZAKRES", "BYŁO", "JEST"];
  svg += `<rect x="${margin}" y="${y}" width="${width - margin * 2}" height="38" fill="#DCE6EC"/>`;
  heads.forEach((h, i) => { svg += `<text x="${cols[i] + 10}" y="${y + 25}" class="v1header" style="fill:${NAVY}">${h}</text>`; });
  y += 38;
  rows.forEach((row, index) => {
    svg += `<rect x="${margin}" y="${y}" width="${width - margin * 2}" height="${rowH}" fill="${index % 2 ? PANEL_ALT : PANEL}"/>`;
    const cells = [`${row.group}${row.tv ? " · TV" : ""}`, row.tee == null ? "—" : String(row.tee), row.time, row.segment, row.was, row.now];
    cells.forEach((cell, i) => {
      const strong = i === 5;
      svg += `<text x="${cols[i] + 10}" y="${y + 29}" class="${strong ? "v1cellStrong" : "v1cell"}" style="fill:${strong ? "#B85F00" : INK}">${xml(fitText(cell, (cols[i + 1] || width - margin) - cols[i] - 20, 17, strong))}</text>`;
    });
    y += rowH;
  });
  if (dutyRows) {
    y += 46;
    svg += `<text x="${margin}" y="${y}" class="v1section" style="fill:${INK}">ZMIANA GODZIN PRACY (pomarańczowo = wcześniej lub dłużej niż w zatwierdzonym planie)</text>`;
    y += 22;
    for (const item of duty) {
      svg += `<text x="${margin + 10}" y="${y + 22}" class="v1cell" style="fill:${INK}">${xml(item.name)}</text><text x="${margin + 420}" y="${y + 22}" class="v1cell" style="fill:${INK}">${xml(item.was)}</text><text x="${margin + 700}" y="${y + 22}" class="v1cellStrong" style="fill:${item.earlier ? "#E07B00" : INK}">→ ${xml(item.now)}</text>`;
      y += 36;
    }
  }
  svg += `</svg>`;
  await svgToPng(svg, width, height, `${safeFilename(tournamentName)}-${round}-plan-rezerwowy-zmiany.png`);
}
